import { GoogleMapsProvider, hasGoogleMapsKey } from "@/components/ui/GoogleMapsProvider";
import { PlacesAutocomplete, type PlaceAddressData } from "@/components/ui/PlacesAutocomplete";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";

interface AddressSearchFieldProps {
  onPlaceSelect: (data: PlaceAddressData) => void;
  placeholder?: string;
  label?: string | null;
  value?: string;
  error?: boolean;
  onChange?: (value: string) => void;
  /** data-testid on the underlying input (E2E hook, both branches). */
  inputTestId?: string;
}

export function AddressSearchField({ onPlaceSelect, placeholder, label, value, error, onChange, inputTestId }: AddressSearchFieldProps) {
  const { t, language } = useLanguage();

  const labelEl = label !== null ? (
    <Label className="text-sm font-medium">
      {label ?? t("البحث عن عنوان", "Search address")}
    </Label>
  ) : null;

  // Fallback to plain text input when Google Maps key is not configured
  if (!hasGoogleMapsKey) {
    return (
      <div className="space-y-2">
        {labelEl}
        <Input
          data-testid={inputTestId}
          placeholder={placeholder ?? t("أدخل العنوان", "Enter address")}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
          className={error ? "border-destructive" : ""}
        />
      </div>
    );
  }

  return (
    <GoogleMapsProvider>
      <div className="space-y-2">
        {labelEl}
        <PlacesAutocomplete
          inputTestId={inputTestId}
          onPlaceSelect={onPlaceSelect}
          onChange={onChange}
          placeholder={placeholder ?? t("ابحث عن عنوان...", "Search for an address...")}
          language={language}
          externalValue={value}
          className={error ? "border-destructive" : ""}
        />
      </div>
    </GoogleMapsProvider>
  );
}

export type { PlaceAddressData } from "@/components/ui/PlacesAutocomplete";
